import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  DocumentApprovalRequestType,
  DocumentSuggestion,
  DocumentTemplateType,
  DocumentType,
} from '../components/document-wizard/document.types';
import { DocumentSourceEnum } from '../components/document-wizard/document-editor/constants/EditorConstats';
import { ImportedDocumentSupportedContentTypes } from '../components/shared/modals/fileUploadModal/types';
import { CounterpartyPermissionLevel } from '../types/counterparty-permissions';

export interface DocumentSigner {
  id: string;
  order: number;
  embeddedSigningUrl: string;
  email: string;
  signedAt: string;
  token: string;
}

export interface DocumentSignerWithSignedAt extends Omit<DocumentSigner, 'signedAt' | 'embeddedSigningUrl' | 'token'> {
  signedAt?: string | null;
}

export type DocumentStateType = {
  id: string | null;
  status: null | string;
  document: DocumentType | null;
  template: DocumentTemplateType | null;
  approvalRequest: DocumentApprovalRequestType | null;
  currentTeamPossession: boolean;
  isEditable: boolean;
  isEligibleForSigningPrep: boolean;
  isTitleEditable: boolean;
  receiverCollaborationEnabled: boolean;
  receiverPagePdfEditingEnabled: boolean;
  counterpartyPermissionLevel: CounterpartyPermissionLevel;
  title: string | null;
  name: string | null;
  documentSuggestions: DocumentSuggestion[];
  isEditorDisabled: boolean;
  isCompareModeEnabled: boolean;
  isEditing: boolean;
  isPending: boolean;
  isEditorLoaded: boolean;
  lastUpdated: string;
  receiverTeamId: string | null;
  teamId: string | null;
  eversignPdfUrl: string | null;
  signedPdfUrl: string | null;
  eversignStatus: string;
  sessionActivities: string[];
  isRejectedSuggestion: boolean;
  shouldReturnForReceiverReview: boolean;
  restrictedEditingMode: boolean;
  trackChangesMode: boolean;
  source: DocumentSourceEnum | null;
  importedAssetUrl: string | null;
  contentType: ImportedDocumentSupportedContentTypes | null;
  generatedDocumentPdfUrl: string | null;
  signers: DocumentSigner[];
  useSignerOrder?: boolean;
  placeholderRefreshKey: number | null;
};

export const initialDocumentState: DocumentStateType = {
  id: null,
  status: null,
  document: null,
  template: null,
  approvalRequest: null,
  currentTeamPossession: false,
  isEditorDisabled: false,
  receiverCollaborationEnabled: false,
  receiverPagePdfEditingEnabled: false,
  counterpartyPermissionLevel: CounterpartyPermissionLevel.CommentOnly,
  isEligibleForSigningPrep: false,
  isCompareModeEnabled: false,
  isEditable: true,
  isTitleEditable: true,
  title: null,
  name: null,
  documentSuggestions: [],
  lastUpdated: '',
  isEditing: false,
  isPending: false,
  isEditorLoaded: false,
  receiverTeamId: null,
  teamId: null,
  eversignPdfUrl: null,
  signedPdfUrl: null,
  eversignStatus: '',
  sessionActivities: [],
  isRejectedSuggestion: false,
  shouldReturnForReceiverReview: false,
  restrictedEditingMode: false,
  trackChangesMode: false,
  source: null,
  importedAssetUrl: null,
  contentType: null,
  generatedDocumentPdfUrl: null,
  signers: [],
  useSignerOrder: undefined,
  placeholderRefreshKey: 0,
};

type DocumentContextType = {
  state: DocumentStateType;
  stateRef: React.MutableRefObject<DocumentStateType>;
  updateDocumentState: (partial: Partial<DocumentStateType>) => void;
  resetDocumentState: () => void;
};

const defaultStateRef = { current: initialDocumentState };
const noop = () => {};

const defaultContextValue: DocumentContextType = {
  state: initialDocumentState,
  stateRef: defaultStateRef as React.MutableRefObject<DocumentStateType>,
  updateDocumentState: noop,
  resetDocumentState: noop,
};

const DocumentContext = createContext<DocumentContextType>(defaultContextValue);

export const DocumentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DocumentStateType>(initialDocumentState);
  const stateRef = useRef<DocumentStateType>(initialDocumentState);

  // Keep ref in sync with state for synchronous reads (autosave, CKEditor callbacks)
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const updateDocumentState = useCallback((partial: Partial<DocumentStateType>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetDocumentState = useCallback(() => {
    setState(initialDocumentState);
  }, []);

  return (
    <DocumentContext.Provider value={{ state, stateRef, updateDocumentState, resetDocumentState }}>
      {children}
    </DocumentContext.Provider>
  );
};

export function useDocumentState(): DocumentContextType {
  return useContext(DocumentContext);
}
